import React, { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@telegram-apps/telegram-ui";
import { TokenIcon, TokenIconSize } from "~/components/TokenIcon";
import { Chain } from "~/types";
import { IconFavoriteFilled } from "../Iconfont/Iconfont";
import { fetchChainList } from "~/api/endpoints";
import useSWR, { preload } from "swr";

type ChainCapsuleTabProps = {
    chainId: string;
    onChainChange: (chain: Chain) => void;
};

export const FAVORITE = "favorite";

const ChainTabItem: React.FC<ChainCapsuleTabProps & { chain: Chain }> = ({
    chainId,
    chain,
    onChainChange,
}) => {
    const isSelected = String(chain.id) === String(chainId);
    return (
        <Button
            data-id={chain.id}
            mode={"gray"}
            style={
                {
                    height: "2.25rem",
                    minWidth: "2.25rem",
                    padding: "0 0.3125rem",
                    borderRadius: "0.625rem",
                    borderColor: isSelected
                        ? "var(--color_text_1)"
                        : "var(--color_border_2)",
                    borderWidth: isSelected ? "1px" : "0.5px",
                    borderStyle: "solid",
                    color: isSelected
                        ? "var(--color_text_1)"
                        : "var(--color_text_1)",
                    backgroundColor: "var(--color_bg_top)",
                } as React.CSSProperties
            }
            onClick={() => {
                onChainChange(chain);
            }}
        >
            <div className="flex items-center">
                <span className="w-[24px] h-[24px] inline-block">
                    {chain.id === FAVORITE ? (
                        <IconFavoriteFilled className="text-h4" />
                    ) : (
                        <TokenIcon
                            icon={chain.logo}
                            size={TokenIconSize.miniMd}
                        />
                    )}
                </span>
                {isSelected && (
                    <span className="ml-1 text-body2 text-text_1 font-semibold">
                        {chain.name}
                    </span>
                )}
            </div>
        </Button>
    );
};

const fetcher = () => fetchChainList().then((res) => res.data?.chains ?? []);
export const preloadChainsList = () => {
    return preload(["ChainCapsuleTab:fetchChainList"], fetcher);
};

const ChainCapsuleTab: React.FC<ChainCapsuleTabProps> = ({
    chainId,
    onChainChange,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslation();

    const { data: chainsListData } = useSWR(
        ["ChainCapsuleTab:fetchChainList"],
        fetcher,
        {
            keepPreviousData: true,
            revalidateIfStale: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
        }
    );

    const chainsList = chainsListData ?? [];

    const firstChainId = useMemo(() => chainsList[0]?.id, [chainsList]);

    useEffect(() => {
        if (chainsList.length === 0) return;

        const containerEl = containerRef.current as HTMLDivElement;
        const button = containerEl?.querySelector<HTMLButtonElement>(
            `button[data-id="${chainId}"]`
        );

        if (button) {
            button?.scrollIntoView({
                block: "nearest",
                inline: "center",
                behavior: "instant",
            });
        } else {
            setTimeout(() => {
                const button = containerEl?.querySelector<HTMLButtonElement>(
                    `button[data-id="${chainId}"]`
                );
                button?.scrollIntoView({
                    block: "nearest",
                    inline: "center",
                    behavior: "instant",
                });
            }, 120);
        }
    }, [chainId, chainsList]);

    return (
        <div className="flex-none relative w-full overflow-x-scroll whitespace-nowrap hidden-scrollbar">
            <div
                className="h-13.5 inline-flex items-center space-x-2 px-4"
                ref={containerRef}
            >
                <ChainTabItem
                    key={chainId}
                    chainId={chainId ?? firstChainId}
                    onChainChange={onChainChange}
                    chain={{
                        id: FAVORITE,
                        name: t("favorite"),
                        logo: "",
                    }}
                />

                {chainsList.map((item) => (
                    <ChainTabItem
                        key={item.id}
                        chain={item}
                        chainId={chainId ?? firstChainId}
                        onChainChange={onChainChange}
                    />
                ))}
            </div>
        </div>
    );
};

export default ChainCapsuleTab;
